/**
 * Överenskommelse om evakueringslägenhet vid renovering (Formulär R-09).
 *
 * A temporary side-agreement to the bostadshyresavtal: the tenant lives in
 * an evacuation apartment while the ordinary one is renovated. A separate
 * waiver of besittningsskydd for the evacuation apartment is a mandatory
 * bilaga, referenced by number.
 *
 * Legal text verbatim from `base-templates/avtal om
 * evakueringslägenhet.docx` (Haymanot-approved).
 */
import { list, nodes, p, section, strong, t, table, val } from '@hyranai/regta-document-ir';
import { annexRefs, fieldRows, hasAttachment, numVal, optionVal, partySlot, propertySlot, referencedAttachments, selectSlot, textVal, } from '../common/index.js';
import { ROOM_OPTIONS, legalMeta, legalSigningBlock } from '../meddelande_18e/kit.js';
const LEGAL = legalMeta('base-templates/avtal om evakueringslägenhet.docx');
const KEY = 'evakuering';
export const parter = {
    ...LEGAL,
    blockKey: `${KEY}.parter-och-fastighet`,
    version: 'v1',
    title: 'Parter och fastighet',
    summary: 'Landlord, tenant(s) and the property of the ordinary apartment.',
    slots: [
        partySlot('landlord_name', 'Hyresvärdens namn', 'lessor', 0, 'name', { required: true }),
        partySlot('landlord_org_number', 'Organisationsnummer', 'lessor', 0, 'legalId', { required: true }),
        partySlot('tenant_name', 'Namn (hyresgäst 1)', 'lessee', 0, 'name', { required: true }),
        partySlot('tenant_personnummer', 'Personnummer (hyresgäst 1)', 'lessee', 0, 'legalId', { help: 'PII — ej sökbart' }),
        partySlot('cotenant_name', 'Namn (hyresgäst 2)', 'lessee', 1, 'name'),
        partySlot('cotenant_personnummer', 'Personnummer (hyresgäst 2)', 'lessee', 1, 'legalId', { help: 'PII — ej sökbart' }),
        propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
    ],
    compile: (input) => [
        section('parter-och-fastighet', 'Parter och fastighet', [
            ...nodes(fieldRows([
                ['Hyresvärd', [textVal(input, 'landlord_name')]],
                ['Organisationsnummer', [textVal(input, 'landlord_org_number')]],
                ['Hyresgäst 1', [textVal(input, 'tenant_name')]],
                ['Personnummer (hyresgäst 1)', [textVal(input, 'tenant_personnummer')]],
                ['Hyresgäst 2', [textVal(input, 'cotenant_name')]],
                ['Personnummer (hyresgäst 2)', [textVal(input, 'cotenant_personnummer')]],
                ['Fastighet', [textVal(input, 'property_designation')]],
            ])),
        ]),
    ],
};
export const bakgrund = {
    ...LEGAL,
    blockKey: `${KEY}.bakgrund`,
    version: 'v1',
    title: 'Bakgrund',
    summary: 'The ordinary apartment is uninhabitable during renovation, so the parties agree a temporary evacuation.',
    slots: [],
    compile: () => [
        section('bakgrund', 'Bakgrund', [
            p('Hyresvärden genomför renoveringsarbeten i den lägenhet som hyresgästen/-erna (”Hyresgästen”) förhyr. Under tiden som hyresvärden genomför dessa åtgärder, kommer den lägenhet som hyresgästen förhyr vara obeboelig, varför hyresvärden och Hyresgästen (”Parterna”) kommer överens om att Hyresgästen tillfälligt ska bo i en evakueringslägenhet under tiden som arbetena pågår. Med anledning av detta har Parterna kommit överens om detta evakueringsavtal, som utgör ett tillfälligt tilläggsavtal mellan hyresavtalsparterna.'),
        ]),
    ],
};
const rentCell = (ctx, input, key) => [
    { ...numVal(ctx, input, key), format: 'currency' },
    t(' kr'),
];
export const lagenheter = {
    ...LEGAL,
    blockKey: `${KEY}.ordinarie-och-evakueringslagenhet`,
    version: 'v1',
    title: 'Ordinarie och evakueringslägenhet',
    summary: 'A two-row table comparing the ordinary and the evacuation apartment.',
    slots: [
        { key: 'apartment_official_number', label: 'Objektnummer ordinarie lägenhet', type: 'text', required: true },
        propertySlot('apartment_address', 'Adress ordinarie lägenhet', 'address', { type: 'textarea', required: true }),
        propertySlot('apartment_property', 'Fastighet ordinarie lägenhet', 'designation', {
            help: 'Fastighetsbeteckning för den ordinarie lägenheten.',
        }),
        selectSlot('apartment_room_count', 'Antal rum ordinarie lägenhet', ROOM_OPTIONS),
        { key: 'apartment_monthly_rent', label: 'Månadshyra ordinarie lägenhet (SEK)', type: 'currency', min: 0 },
        { key: 'evac_object_number', label: 'Objektnummer evakueringslägenhet', type: 'text', required: true },
        { key: 'evac_address', label: 'Adress evakueringslägenhet', type: 'textarea', required: true },
        {
            key: 'evac_property',
            label: 'Fastighet evakueringslägenhet',
            type: 'text',
            help: 'Fastighetsbeteckning för evakueringsfastigheten (om den skiljer sig från ordinarie).',
        },
        selectSlot('evac_room_count', 'Antal rum evakueringslägenhet', ROOM_OPTIONS),
        { key: 'evac_monthly_rent', label: 'Månadshyra evakueringslägenhet (SEK)', type: 'currency', required: true, min: 0 },
    ],
    compile: (input, ctx) => {
        const row = (title, prefix, numberKey) => [
            [strong(title)],
            [textVal(input, numberKey)],
            [textVal(input, `${prefix}_address`, 'long')],
            [textVal(input, `${prefix}_property`)],
            [optionVal(ROOM_OPTIONS, input, `${prefix}_room_count`)],
            rentCell(ctx, input, `${prefix}_monthly_rent`),
        ];
        return [
            section('ordinarie-och-evakueringslagenhet', 'Ordinarie och evakueringslägenhet', [
                p('Under den tid som den förhyrda lägenheten genomgår renoveringsarbeten, får Hyresgästen tillfälligt ta i anspråk följande evakueringslägenhet:'),
                table(['', 'Objektnummer', 'Adress', 'Fastighet', 'Antal rum', 'Månadshyra'].map((header, i) => ({ key: `c${i}`, header: [t(header)], width: 16 })), [
                    row('Ordinarie lägenhet', 'apartment', 'apartment_official_number'),
                    row('Evakueringslägenhet', 'evac', 'evac_object_number'),
                ], { variant: 'plain', keepTogether: true }),
            ]),
        ];
    },
};
export const hyra = {
    ...LEGAL,
    blockKey: `${KEY}.hyra-under-evakueringen`,
    version: 'v1',
    title: 'Hyra under evakueringen',
    summary: 'The tenant pays the lower of the two rents; the evacuation apartment is taken as-is.',
    slots: [],
    compile: () => [
        section('hyra-under-evakueringen', 'Hyra under evakueringen', [
            p('Hyresgästen ska betala hyra för evakueringslägenheten under den tid som hyresgästen är evakuerad. Om hyresgästen har blivit tilldelad en lägenhet som har en högre hyra än sin ordinarie hyra, ska Hyresgästen endast betala den ordinarie hyran. Om Hyresgästen i stället fått en lägenhet som har en lägre hyra än sin ordinarie hyra, ska Hyresgästen betala den lägre hyran. Detta kommer att korrigeras med ett hyresavdrag på evakueringslägenhetens hyresavier.'),
            p('Genom undertecknande av denna överenskommelse godkänner Hyresgästen att lägenheten uthyrs i befintligt skick och att Hyresgästen är införstådd med att inga underhållsåtgärder görs inför och under tiden som Hyresgästen är evakuerad.'),
        ]),
    ],
};
export const ataganden = {
    ...LEGAL,
    blockKey: `${KEY}.hyresgastens-ataganden`,
    version: 'v1',
    title: 'Hyresgästens åtaganden',
    summary: 'Move back when notified, sign the besittningsskydd waiver (bilaga), and hand back the evacuation apartment cleaned.',
    slots: [
        {
            key: 'notice_period_months',
            label: 'Varseltid för återflytt (månader)',
            type: 'number',
            default: 1,
            min: 0,
            integer: true,
            help: 'Hyresvärden ska meddela tidpunkt för återflytt senast så här många månader före planerat återflytt.',
        },
        {
            key: 'possession_waiver_annex',
            label: 'Bilaga – avstående från besittningsskydd',
            type: 'attachment',
            required: true,
            help: 'Obligatorisk bilaga: överenskommelsen om avstående av besittningsskydd för evakueringslägenheten. Välj eller ladda upp den.',
        },
    ],
    compile: (input, ctx) => {
        const annex = input.possession_waiver_annex;
        const annexRef = annexRefs(ctx, annex) ?? (hasAttachment(annex) ? [val(null, { sourceKey: 'possession_waiver_annex', blankWidth: 'medium' })] : [t('Bilaga')]);
        const out = [
            section('hyresgastens-ataganden', 'Hyresgästens åtaganden', [
                p('Hyresgästen förbinder sig att:'),
                list([
                    [
                        t('Flytta tillbaka till sin ordinarie lägenhet när den är klar för inflyttning, om inte parterna skriftligen kommer överens om annat. Hyresvärden ska meddela tidpunkt för återflytt så snart som möjligt, men senast '),
                        numVal(ctx, input, 'notice_period_months'),
                        t(' månad före planerat återflytt.'),
                    ],
                    [t('Ingå avtal om avstående av besittningsskydd för evakueringslägenheten ('), ...annexRef, t(').')],
                    'Lämna evakueringslägenheten väl städad och återlämna tillhörande nycklar till hyresvärden senast kl. 12.00 den första vardagen efter aviserat återflyttsdatum.',
                ]),
            ]),
        ];
        return { nodes: out, attachments: referencedAttachments(ctx, annex) };
    },
};
export const varRadd = {
    ...LEGAL,
    blockKey: `${KEY}.var-radd-om-evakueringslagenheten`,
    version: 'v1',
    title: 'Var rädd om evakueringslägenheten',
    summary: 'Duty of care, inspection on moving in and out, and folkbokföring / mail forwarding.',
    slots: [],
    compile: () => [
        section('var-radd-om-evakueringslagenheten', 'Var rädd om evakueringslägenheten', [
            p('Notera att Hyresgästen är skyldig att väl vårda den tillfälliga lägenheten och även i övrigt iaktta de skyldigheter som påkommer en hyresgäst enligt lag och vid var tid gällande ordningsregler för fastigheten.'),
            p('Lägenheten besiktigas innan du flyttar in och efter att du flyttat ut. Om något fattas eller har gått sönder, förbehåller hyresvärden sig rätten att debitera Hyresgästen för uppkommen skada / brist.'),
            p('Hyresgästen ansvarar för att iaktta folkbokföringslagens regler under evakueringstiden och att eftersända post till evakueringsadressen.'),
        ]),
    ],
};
export const underskrift = legalSigningBlock(LEGAL, {
    blockKey: `${KEY}.underskrift`,
    title: 'Underskrifter',
    summary: 'Signing statement and panels: tenant 1, an eventual tenant 2, and the landlord’s representative.',
    defaults: {
        ref: 'underskrift',
        title: 'Underskrifter',
        statement: true,
        parties: [
            { label: 'Hyresgäst 1', role: 'lessee', ordinal: 0 },
            { label: 'Eventuell hyresgäst 2', role: 'lessee', ordinal: 1 },
            { label: 'Hyresvärdens representant', role: 'lessor', ordinal: 0 },
        ],
    },
});
export const EVAKUERING_BLOCKS = [parter, bakgrund, lagenheter, hyra, ataganden, varRadd, underskrift];
//# sourceMappingURL=blocks.js.map